import React, { useState, useEffect } from 'react';
import {
  Moon,
  Sun,
  Zap,
  FileJson,
  ChevronDown,
  Check,
  Key,
  X,
  AlertCircle,
  Loader2,
  DollarSign,
  FileDown,
} from 'lucide-react';
import { useStore } from '../store/useStore';
import {
  validateLicenseKey,
  storeLicense,
  validateStoredLicense,
  LicenseValidationError,
} from '../services/licenseService';
import { PricingPage } from './PricingPage';
import { ExportPDFDialog } from './ExportPDFDialog';

export const Header: React.FC = () => {
  const {
    isDarkMode,
    toggleDarkMode,
    workflows,
    currentWorkflowId,
    setCurrentWorkflow,
    fileName,
    reset,
    isUnlocked,
    setIsUnlocked,
  } = useStore();

  const [isWorkflowMenuOpen, setIsWorkflowMenuOpen] = useState(false);
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const [isPricingPageOpen, setIsPricingPageOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [licenseKeyInput, setLicenseKeyInput] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Check for stored license on mount
  useEffect(() => {
    const checkStoredLicense = async () => {
      const isValid = await validateStoredLicense();
      if (isValid) {
        setIsUnlocked(true);
      }
    };

    checkStoredLicense();
  }, [setIsUnlocked]);

  const currentWorkflow = workflows.find((w) => w.id === currentWorkflowId);

  const handleNewFile = () => {
    if (
      confirm('This will clear the current workflow. Continue?')
    ) {
      reset();
    }
  };

  const handleLicenseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    setIsValidating(true);

    try {
      const result = await validateLicenseKey(licenseKeyInput);

      if (result.valid) {
        // License is valid - unlock and store it
        storeLicense(licenseKeyInput);
        setIsUnlocked(true);
        setIsLicenseModalOpen(false);
        setLicenseKeyInput('');
      } else {
        // License is invalid - show error
        setValidationError(result.error || 'Invalid license key');
      }
    } catch (error) {
      if (error instanceof LicenseValidationError) {
        setValidationError(error.message);
      } else {
        setValidationError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsValidating(false);
    }
  };

  const handleGetLicense = () => {
    const checkoutUrl = import.meta.env.VITE_LEMON_SQUEEZY_CHECKOUT_URL;

    if (checkoutUrl && checkoutUrl !== 'https://store.lemonsqueezy.com/checkout') {
      window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
    } else {
      alert(
        'Checkout URL not configured.\n\n' +
        'Please set VITE_LEMON_SQUEEZY_CHECKOUT_URL in .env.local\n\n' +
        'See LEMON_SQUEEZY_SETUP.md for instructions.'
      );
    }
  };

  return (
    <>
      <header className="h-16 border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40 outline-none">
        <div className="h-full px-4 flex items-center justify-between">
          {/* Left: Logo & Workflow Selector */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Zap className="w-6 h-6 text-primary" />
              <h1 className="text-xl font-bold">ZapLens</h1>
            </div>

            {workflows.length > 0 && (
              <>
                <div className="h-6 w-px bg-border" />
                <div className="relative">
                  <button
                    onClick={() => setIsWorkflowMenuOpen(!isWorkflowMenuOpen)}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-accent rounded-lg transition-colors text-sm"
                  >
                    <FileJson className="w-4 h-4" />
                    <span className="max-w-[200px] truncate">
                      {currentWorkflow?.title || 'Select workflow'}
                    </span>
                    {workflows.length > 1 && (
                      <>
                        <span className="text-xs text-muted-foreground">
                          ({workflows.length})
                        </span>
                        <ChevronDown className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  {isWorkflowMenuOpen && workflows.length > 1 && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsWorkflowMenuOpen(false)}
                      />
                      <div className="absolute top-full left-0 mt-2 w-80 bg-card border border-border rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
                        {workflows.map((workflow) => (
                          <button
                            key={workflow.id}
                            onClick={() => {
                              setCurrentWorkflow(workflow.id);
                              setIsWorkflowMenuOpen(false);
                            }}
                            className={`
                              w-full px-4 py-3 text-left hover:bg-accent transition-colors flex items-center justify-between
                              ${workflow.id === currentWorkflowId ? 'bg-accent' : ''}
                            `}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{workflow.title}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {workflow.nodes.length} steps
                                {workflow.status && (
                                  <span
                                    className={`ml-2 ${
                                      workflow.status === 'on'
                                        ? 'text-emerald-600'
                                        : 'text-muted-foreground'
                                    }`}
                                  >
                                    • {workflow.status}
                                  </span>
                                )}
                              </p>
                            </div>
                            {workflow.id === currentWorkflowId && (
                              <Check className="w-4 h-4 text-primary flex-shrink-0 ml-2" />
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {fileName && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-lg text-sm">
                <FileJson className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground max-w-[150px] truncate">
                  {fileName}
                </span>
              </div>
            )}

            {workflows.length > 0 && (
              <>
                <button
                  onClick={() => setIsExportDialogOpen(true)}
                  className="px-3 py-2 hover:bg-accent rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
                  aria-label="Export to PDF"
                >
                  <FileDown className="w-4 h-4" />
                  <span className="hidden sm:inline">Export PDF</span>
                </button>

                {!isUnlocked && (
                  <button
                    onClick={() => setIsLicenseModalOpen(true)}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium text-sm flex items-center gap-2"
                  >
                    <Key className="w-4 h-4" />
                    <span className="hidden sm:inline">Unlock Pro ($29)</span>
                  </button>
                )}

                <button
                  onClick={handleNewFile}
                  className="px-4 py-2 bg-muted hover:bg-accent rounded-lg transition-colors text-sm"
                >
                  New File
                </button>
              </>
            )}

            <button
              onClick={() => setIsPricingPageOpen(true)}
              className="px-3 py-2 hover:bg-accent rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
              aria-label="View pricing"
            >
              <DollarSign className="w-4 h-4" />
              <span className="hidden sm:inline">Pricing</span>
            </button>

            <button
              onClick={toggleDarkMode}
              className="p-2 hover:bg-accent rounded-lg transition-colors"
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* License Key Modal */}
      {isLicenseModalOpen && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl shadow-2xl max-w-md w-full animate-fade-in">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Key className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-xl font-bold">Enter License Key</h2>
              </div>
              <button
                onClick={() => setIsLicenseModalOpen(false)}
                className="p-2 hover:bg-accent rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleLicenseSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">License Key</label>
                <input
                  type="text"
                  value={licenseKeyInput}
                  onChange={(e) => {
                    setLicenseKeyInput(e.target.value);
                    setValidationError(null); // Clear error on input change
                  }}
                  placeholder="XXXX-XXXX-XXXX-XXXX"
                  className="w-full px-4 py-2 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                  autoFocus
                  disabled={isValidating}
                />
              </div>

              {/* Validation Error */}
              {validationError && (
                <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <p>{validationError}</p>
                </div>
              )}

              <div className="bg-muted/50 border border-border rounded-lg p-4">
                <h3 className="font-medium mb-3 text-sm">ZapLens Pro - $29 Lifetime</h3>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span>View complete step configurations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span>Access mapped variables and field references</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span>See code snippets and filter logic</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span>Export and copy raw node data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span><strong>Lifetime access</strong> - pay once, use forever</span>
                  </li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={isValidating || !licenseKeyInput.trim()}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isValidating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    'Activate License'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsLicenseModalOpen(false);
                    setValidationError(null);
                    setLicenseKeyInput('');
                  }}
                  disabled={isValidating}
                  className="px-4 py-2 bg-muted hover:bg-accent rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>

              <p className="text-xs text-center text-muted-foreground">
                Don&apos;t have a license?{' '}
                <button
                  type="button"
                  onClick={handleGetLicense}
                  className="text-primary hover:underline"
                >
                  Get one now
                </button>
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Pricing Page Modal */}
      <PricingPage
        isOpen={isPricingPageOpen}
        onClose={() => setIsPricingPageOpen(false)}
        onGetLicense={() => {
          setIsPricingPageOpen(false);
          handleGetLicense();
        }}
      />

      {/* PDF Export Dialog */}
      <ExportPDFDialog
        isOpen={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
      />
    </>
  );
};
